import semver from 'semver'
import { resolve } from 'app/releases'
import { create } from 'app/utils/data'
import posthog from 'app/utils/posthog'
import { kitOfferPlanSlug } from 'app/views/icons/kitOfferPlan'

const STORAGE_KEY_ICONS = 'fa-icon-tray-icons'
const STORAGE_KEY_KIT = 'fa-icon-tray-kit'
const STORAGE_KEY_VERSION = 'fa-icon-tray-version'
const STORAGE_KEY_CHECKOUT = 'fa-icon-tray-checkout'

// Matches the server-side cap on preliminary Kit selections.
export const MAX_SELECTION_ICONS = 1000
export const SELECTION_FULL_ERROR = 'selection_full'

const CHECKOUT_MARKER_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
const ROLLOUT_STAGES = ['off', 'free_signed_in', 'everyone']

function matchesIcon(a, b) {
  return a.name === b.name && a.family === b.family && a.style === b.style
}

// Unknown or missing values fail closed.
export function selectionRollout() {
  const value = typeof window !== 'undefined' ? window.__icon_selection_rollout__ : undefined
  return ROLLOUT_STAGES.includes(value) ? value : 'off'
}

function subsetKey({ family, style, name }) {
  return `${family}-${style}-${name}`
}

function planOmitsProPlus(planSlug) {
  return planSlug === 'starter' || planSlug === 'starter-monthly'
}

// Whether `kit` was built from this selection. Starter Kits leave out Pro+ icons,
// so those aren't required to match.
export function kitContainsSelection(kit, icons, planSlug) {
  if (kit?.subset?.type !== 'custom') return false

  // An empty subset is saved without an icons key (a Starter Kit from an all-Pro+ selection).
  const kitKeys = new Set()
  ;(kit.subset.icons || []).forEach((record) => {
    ;(record.only || []).forEach((name) => kitKeys.add(subsetKey({ family: record.family, style: record.style, name })))
  })

  return icons
    .filter((icon) => !(planOmitsProPlus(planSlug) && icon.proPlus))
    .every((icon) => kitKeys.has(subsetKey({ ...normalizeIconForSubset(icon), name: icon.name })))
}

// Kit timestamps are naive UTC; the marker comes from the browser clock, so allow some skew.
const CLOCK_SKEW_MS = 10 * 60 * 1000

function parseUtc(timestamp) {
  if (typeof timestamp !== 'string') return NaN
  return Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(timestamp) ? timestamp : `${timestamp}Z`)
}

// The newest Kit created after checkout started that holds the selection.
export function findKitBuiltFromSelection(kits, icons, marker) {
  const startedAt = Date.parse(marker?.startedAt)
  if (Number.isNaN(startedAt)) return undefined

  return (kits || [])
    .filter((kit) => parseUtc(kit.insertedAt) >= startedAt - CLOCK_SKEW_MS)
    .filter((kit) => kitContainsSelection(kit, icons, marker.planSlug))
    .sort((a, b) => parseUtc(b.insertedAt) - parseUtc(a.insertedAt))[0]
}

export function newestKit(kits) {
  return [...(kits || [])].sort((a, b) => parseUtc(b.insertedAt) - parseUtc(a.insertedAt))[0]
}

function writeStorage(key, value) {
  try {
    if (value === null || value === undefined) {
      localStorage.removeItem(key)
    } else {
      localStorage.setItem(key, JSON.stringify(value))
    }
  } catch (e) {
    // Graceful fallback if localStorage is unavailable or quota exceeded
  }
}

function readStorage(key) {
  try {
    const json = localStorage.getItem(key)
    return json ? JSON.parse(json) : null
  } catch (e) {
    return null
  }
}

function isFreshCheckoutMarker(marker) {
  const startedAt = Date.parse(marker?.startedAt)
  return !Number.isNaN(startedAt) && Date.now() - startedAt <= CHECKOUT_MARKER_MAX_AGE_MS
}

export function normalizeIconForSubset(icon) {
  let family = icon.family
  let style = icon.style

  if (icon.family === 'brands') {
    family = 'classic'
    style = 'brands'
  }

  if (icon.style === 'duotone') {
    family = 'duotone'
    style = 'solid'
  }

  return { family, style }
}

function addIconsToSubset(subset, icons) {
  icons.forEach((icon) => {
    const { family, style } = normalizeIconForSubset(icon)
    let record = subset.icons.find((r) => r.family === family && r.style === style)

    if (!record) {
      record = { family, style, only: [] }
      subset.icons.push(record)
    }

    if (!record.only.includes(icon.name)) {
      record.only.push(icon.name)
    }
  })

  return subset
}

function saveIconsToStorage(icons) {
  try {
    localStorage.setItem(STORAGE_KEY_ICONS, JSON.stringify(icons))
  } catch (e) {
    // Graceful fallback if localStorage is unavailable or quota exceeded
  }
}

export default {
  namespaced: true,

  state: {
    collectedIcons: [],
    selectedKitToken: null,
    saving: false,
    error: null,
    successMessage: null,
    showNoCustomKitModal: false,
    showLoginBanner: false,
    pendingIcon: null,
    searchMajorVersion: null,
    familyStyleWarnings: [],
    // When true, the selection bar stays visible even with no collected icons —
    // set when the user arrives from a Kit's "Add icons" button.
    pinned: false,
    // Search major the selection was collected from, kept so checkout knows it
    // after a reload on the purchase page.
    selectionMajorVersion: null,
    checkoutMarker: null,
    showCreateYourKitModal: false
  },

  getters: {
    collectedIcons: (state) => state.collectedIcons,
    collectedIconCount: (state) => state.collectedIcons.length,
    selectedKitToken: (state) => state.selectedKitToken,
    hasCollectedIcons: (state) => state.collectedIcons.length > 0,
    eligibleKits: (state, getters, rootState, rootGetters) => {
      const kits = rootGetters['kits/activeKits'] || []
      const searchMajor = state.searchMajorVersion
      return kits
        .filter((kit) => {
          if (kit.subset?.type !== 'custom') return false
          if (!searchMajor || !kit.version) return true
          const resolved = resolve({ version: kit.version })
          const kitMajor = semver.coerce(resolved)
          return kitMajor && kitMajor.major === searchMajor
        })
        .map((kit) => ({ name: kit.name, token: kit.token, updatedAt: kit.updatedAt }))
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
    },
    saving: (state) => state.saving,
    error: (state) => state.error,
    successMessage: (state) => state.successMessage,
    showNoCustomKitModal: (state) => state.showNoCustomKitModal,
    showLoginBanner: (state) => state.showLoginBanner,
    pinned: (state) => state.pinned,
    pendingIcon: (state) => state.pendingIcon,
    canCreateMoreKits: (state, getters, rootState, rootGetters) => {
      const permitsFind = rootGetters['permits/find']
      if (!permitsFind) return false

      const result = permitsFind('accessToKits')
      const kitLimit = (result && result.permit && result.permit.kitLimit) || 0
      if (kitLimit === -1) return true

      const activeKits = rootGetters['kits/activeKits'] || []
      return activeKits.length < kitLimit
    },
    newIcons: (state, getters) => {
      const subset = getters.selectedKitSubsetIcons
      if (!subset || subset.size === 0) return state.collectedIcons

      return state.collectedIcons.filter((icon) => {
        const { family, style } = normalizeIconForSubset(icon)
        return !subset.has(`${family}-${style}-${icon.name}`)
      })
    },

    newIconCount: (state, getters) => getters.newIcons.length,

    selectedKitSubsetIconList: (state, getters, rootState, rootGetters) => {
      const kitToken = state.selectedKitToken
      if (!kitToken) return []

      const kits = rootGetters['kits/currentKits'] || []
      const kit = kits.find((k) => k.token === kitToken)

      if (!kit || !kit.subset || !kit.subset.icons) return []

      const icons = []
      kit.subset.icons.forEach((record) => {
        if (record.only) {
          record.only.forEach((name) => {
            icons.push({ name, family: record.family, style: record.style })
          })
        }
      })
      return icons
    },

    selectedKitSubsetIcons: (state, getters) => {
      const icons = getters.selectedKitSubsetIconList
      const keys = new Set()
      icons.forEach((icon) => {
        keys.add(`${icon.family}-${icon.style}-${icon.name}`)
      })
      return keys
    },

    selectedKitVersion: (state, getters, rootState, rootGetters) => {
      const kitToken = state.selectedKitToken
      if (!kitToken) return null

      const kits = rootGetters['kits/currentKits'] || []
      const kit = kits.find((k) => k.token === kitToken)
      if (!kit || !kit.version) return null

      return resolve({ version: kit.version })
    },

    versionMismatchIcons: (state, getters) => {
      const kitVersion = getters.selectedKitVersion
      if (!kitVersion) return []

      const coerced = semver.coerce(kitVersion)
      if (!coerced) return []

      return getters.newIcons.filter((icon) => {
        if (!icon.changes || !icon.changes[0]) return false
        const introduced = semver.coerce(icon.changes[0])
        return introduced && semver.gt(introduced, coerced)
      })
    },

    hasVersionMismatch: (state, getters) => getters.versionMismatchIcons.length > 0,

    isPinnedVersion: (state, getters, rootState, rootGetters) => {
      const kitToken = state.selectedKitToken
      if (!kitToken) return false

      const kits = rootGetters['kits/currentKits'] || []
      const kit = kits.find((k) => k.token === kitToken)
      if (!kit || !kit.version) return false

      return !/^\d+\.x$/.test(kit.version) && kit.version !== 'latest'
    },

    familyStyleWarnings: (state) => state.familyStyleWarnings,
    hasFamilyStyleWarnings: (state) => state.familyStyleWarnings.length > 0,

    // null while a signed-in account is still loading, so nothing flips to the
    // Free UI before we know.
    selectionAudience: (state, getters, rootState, rootGetters) => {
      if (!rootGetters['sessions/isSignedIn']) return 'signed_out'

      const account = rootGetters['accounts/currentAccount']
      if (!account) return null

      return account.isFree ? 'free' : 'paid'
    },

    isSelectionEligible: (state, getters) => {
      const rollout = selectionRollout()

      switch (getters.selectionAudience) {
        case 'paid':
          return true
        case 'free':
          return rollout === 'free_signed_in' || rollout === 'everyone'
        case 'signed_out':
          return rollout === 'everyone'
        default:
          return false
      }
    },

    hasProPlusSelection: (state) => state.collectedIcons.some((icon) => icon.proPlus),

    nonProPlusIcons: (state) => state.collectedIcons.filter((icon) => !icon.proPlus),

    // null until permits load, so a Pro+ customer never flashes the upsell.
    hasProPlusAccess: (state, getters, rootState, rootGetters) => {
      if (!rootGetters['permits/all']) return null
      return rootGetters['permits/has']('accessToProPlus')
    },

    // A paid plan without Pro+ can't save Pro+ icons to a Kit (the API answers 403),
    // so these customers get the "Create Your Kit" upsell instead of the Kit picker.
    needsProPlusUpgrade: (state, getters) => getters.selectionAudience === 'paid' && getters.hasProPlusSelection && getters.hasProPlusAccess === false,

    kitOfferPlanSlug: (state, getters) => kitOfferPlanSlug(getters.hasProPlusSelection),

    isSelectionFull: (state) => state.collectedIcons.length >= MAX_SELECTION_ICONS,

    checkoutMarker: (state) => state.checkoutMarker,

    showCreateYourKitModal: (state) => state.showCreateYourKitModal,

    checkoutSelectionPayload: (state) => {
      const major = state.selectionMajorVersion || state.searchMajorVersion

      return {
        version: major ? `${major}.x` : null,
        icons: state.collectedIcons.map((icon) => ({ ...normalizeIconForSubset(icon), name: icon.name }))
      }
    }
  },

  mutations: {
    addIcon(state, { name, family, style, changes, proPlus }) {
      const target = { name, family, style }
      const exists = state.collectedIcons.some((icon) => matchesIcon(icon, target))
      if (!exists) {
        const icon = { name, family, style }
        if (changes) icon.changes = changes
        if (proPlus) icon.proPlus = true
        state.collectedIcons.push(icon)
        saveIconsToStorage(state.collectedIcons)

        if (state.searchMajorVersion) {
          state.selectionMajorVersion = state.searchMajorVersion
          writeStorage(STORAGE_KEY_VERSION, state.selectionMajorVersion)
        }
      }
    },

    // audience is where the buyer started, since the account is paid by the time we report.
    setCheckoutMarker(state, { planSlug, landing = null, audience = null }) {
      state.checkoutMarker = { startedAt: new Date().toISOString(), planSlug, landing, audience }
      writeStorage(STORAGE_KEY_CHECKOUT, state.checkoutMarker)
    },

    setCheckoutMarkerPlan(state, planSlug) {
      if (!state.checkoutMarker) return
      state.checkoutMarker = { ...state.checkoutMarker, planSlug }
      writeStorage(STORAGE_KEY_CHECKOUT, state.checkoutMarker)
    },

    clearCheckoutMarker(state) {
      state.checkoutMarker = null
      writeStorage(STORAGE_KEY_CHECKOUT, null)
    },

    setShowCreateYourKitModal(state, value) {
      state.showCreateYourKitModal = value
    },

    removeIcon(state, { name, family, style }) {
      const target = { name, family, style }
      state.collectedIcons = state.collectedIcons.filter((icon) => !matchesIcon(icon, target))
      saveIconsToStorage(state.collectedIcons)
    },

    setSelectedKit(state, kitToken) {
      state.selectedKitToken = kitToken
    },

    setPinned(state, value) {
      state.pinned = value
    },

    clearAll(state) {
      state.collectedIcons = []
      state.selectedKitToken = null
      state.saving = false
      state.error = null
      state.successMessage = null
      state.showNoCustomKitModal = false
      state.showLoginBanner = false
      state.pendingIcon = null
      state.familyStyleWarnings = []
      // `pinned` is intentionally NOT reset here. clearAll runs after a save and
      // on "Clear Selection"; a user who arrived from a Kit's "Add icons" button
      // should keep the selection bar open to keep adding. It's cleared on
      // sign-out (resetState) and on search visits without a `kit` param.
      state.selectionMajorVersion = null
      state.checkoutMarker = null
      try {
        localStorage.removeItem(STORAGE_KEY_ICONS)
        localStorage.removeItem(STORAGE_KEY_VERSION)
        localStorage.removeItem(STORAGE_KEY_CHECKOUT)
      } catch (e) {
        // Graceful fallback
      }
    },

    loadFromStorage(state) {
      const icons = readStorage(STORAGE_KEY_ICONS)
      if (Array.isArray(icons) && icons.length > 0) {
        state.collectedIcons = icons
      }

      const version = readStorage(STORAGE_KEY_VERSION)
      if (Number.isInteger(version)) {
        state.selectionMajorVersion = version
      }

      const marker = readStorage(STORAGE_KEY_CHECKOUT)
      if (isFreshCheckoutMarker(marker)) {
        state.checkoutMarker = marker
      } else if (marker) {
        state.checkoutMarker = null
        writeStorage(STORAGE_KEY_CHECKOUT, null)
      }
    },

    setSaving(state, value) {
      state.saving = value
    },

    setError(state, message) {
      state.error = message
    },

    setSuccessMessage(state, message) {
      state.successMessage = message
    },

    dismissSuccessMessage(state) {
      state.successMessage = null
    },

    setShowNoCustomKitModal(state, value) {
      state.showNoCustomKitModal = value
    },

    setShowLoginBanner(state, value) {
      state.showLoginBanner = value
    },

    setPendingIcon(state, icon) {
      state.pendingIcon = icon
    },

    dismissBanner(state) {
      state.showNoCustomKitModal = false
      state.showLoginBanner = false
      state.pendingIcon = null
    },

    setSearchMajorVersion(state, majorVersion) {
      state.searchMajorVersion = majorVersion
    },

    setFamilyStyleWarnings(state, warnings) {
      state.familyStyleWarnings = warnings
    },

    resetState(state) {
      state.collectedIcons = []
      state.selectedKitToken = null
      state.saving = false
      state.error = null
      state.successMessage = null
      state.showNoCustomKitModal = false
      state.showLoginBanner = false
      state.pendingIcon = null
      state.searchMajorVersion = null
      state.familyStyleWarnings = []
      state.pinned = false
      state.selectionMajorVersion = null
      state.checkoutMarker = null
      state.showCreateYourKitModal = false
      try {
        localStorage.removeItem(STORAGE_KEY_ICONS)
        localStorage.removeItem(STORAGE_KEY_KIT)
        localStorage.removeItem(STORAGE_KEY_VERSION)
        localStorage.removeItem(STORAGE_KEY_CHECKOUT)
      } catch (e) {
        // Graceful fallback
      }
    }
  },

  actions: {
    async addIcon({ commit, dispatch, getters }, { name, family, style, changes, proPlus }) {
      const audience = getters.selectionAudience

      if (audience !== 'paid' && getters.isSelectionFull) {
        commit('setError', SELECTION_FULL_ERROR)
        return
      }

      const wasEmpty = !getters.hasCollectedIcons
      commit('addIcon', { name, family, style, changes, proPlus })

      if (wasEmpty && audience !== 'paid') {
        posthog?.capture('icon_grid:first_icon_selected', { audience })
      }

      await dispatch('validateFamilyStyleAvailability')
    },

    async removeIcon({ commit, state, dispatch }, { name, family, style }) {
      commit('removeIcon', { name, family, style })
      if (state.error === SELECTION_FULL_ERROR) commit('setError', null)
      if (state.collectedIcons.length === 0) {
        commit('clearAll')
      } else {
        await dispatch('validateFamilyStyleAvailability')
      }
    },

    async selectKit({ commit, dispatch }, kitToken) {
      commit('setSelectedKit', kitToken)
      await dispatch('validateFamilyStyleAvailability')
    },

    // Pin the selection bar open (used when arriving from a Kit's "Add icons"
    // button) and pre-select that Kit when it's eligible for the search version.
    async pinForKit({ commit, dispatch, getters }, kitToken) {
      commit('setPinned', true)
      if (kitToken && getters.eligibleKits.some((kit) => kit.token === kitToken)) {
        await dispatch('selectKit', kitToken)
      }
    },

    async validateFamilyStyleAvailability({ state, getters, commit }) {
      if (!getters.isPinnedVersion) {
        commit('setFamilyStyleWarnings', [])
        return
      }

      const kitVersion = getters.selectedKitVersion
      const newIcons = getters.newIcons
      const apiIcons = newIcons.map((icon) => {
        const { family, style } = normalizeIconForSubset(icon)
        return { name: icon.name, family, style }
      })

      if (apiIcons.length === 0) {
        commit('setFamilyStyleWarnings', [])
        return
      }

      const kitToken = state.selectedKitToken

      try {
        const response = await create({
          url: '/api/clouds/kits/subset-validation',
          type: 'subset-validation',
          data: { version: kitVersion, icons: apiIcons }
        })

        if (state.selectedKitToken !== kitToken) return

        const results = response.data.data.attributes.icons
        const unavailable = results
          .filter((r) => !r.available)
          .map((r) => {
            const lookupName = r.lookup_name || r['lookup-name']
            const original = newIcons.find((i) => {
              if (i.name !== lookupName) return false
              const { family, style } = normalizeIconForSubset(i)
              return family === r.family && style === r.style
            })
            return original || { name: lookupName, family: r.family, style: r.style }
          })
        commit('setFamilyStyleWarnings', unavailable)
      } catch (e) {
        commit('setFamilyStyleWarnings', [])
      }
    },

    // Signed-out selections are loaded too, so they survive sign-in and checkout.
    // Sign-out still clears them, through every module's resetState.
    async initialize({ commit, dispatch, rootGetters }) {
      if (rootGetters['sessions/isSignedIn']) {
        try {
          await dispatch('kits/fetchKits', undefined, { root: true })
        } catch (e) {
          // Kits fetch failed — continue with whatever is in localStorage
        }
      }

      commit('loadFromStorage')
    },

    // FR-025: the icon selection is kept so they can still buy a Pro Kit later.
    async createFreeKit({ state, rootGetters, dispatch }) {
      const defaultProps = rootGetters['kits/defaultKitProps']
      const major = state.selectionMajorVersion || state.searchMajorVersion

      return dispatch(
        'kits/createKit',
        { ...defaultProps, licenseSelected: 'free', version: major ? `${major}.x` : defaultProps.version },
        { root: true }
      )
    },

    async createKitWithoutProPlus({ state, getters, rootGetters, commit, dispatch }) {
      const subset = addIconsToSubset({ type: 'custom', icons: [] }, getters.nonProPlusIcons)
      const defaultProps = rootGetters['kits/defaultKitProps']
      const major = state.selectionMajorVersion || state.searchMajorVersion

      const kit = await dispatch(
        'kits/createKit',
        {
          ...defaultProps,
          licenseSelected: 'pro',
          technologySelected: 'svg',
          version: major ? `${major}.x` : defaultProps.version,
          subset
        },
        { root: true }
      )

      commit('clearAll')
      return kit
    },

    async createKitAndAddIcon({ commit, state, getters, rootGetters, dispatch }) {
      commit('setSaving', true)
      commit('setError', null)

      try {
        const pending = getters.pendingIcon
        const subset = { type: 'custom', icons: [] }

        if (pending) {
          const { family, style } = normalizeIconForSubset(pending)
          subset.icons.push({ family, style, only: [pending.name] })
        }

        const defaultProps = rootGetters['kits/defaultKitProps']
        const version = state.searchMajorVersion ? `${state.searchMajorVersion}.x` : defaultProps.version
        const kitProps = {
          ...defaultProps,
          licenseSelected: 'pro',
          technologySelected: 'svg',
          version,
          subset
        }

        const newKit = await dispatch('kits/createKit', kitProps, { root: true })

        await dispatch('kits/fetchKits', undefined, { root: true })

        commit('setSelectedKit', newKit.token)
        commit('dismissBanner')

        if (pending) {
          commit('setSuccessMessage', { kitName: newKit.name, kitToken: newKit.token, iconCount: 1 })
        }

        posthog?.capture('icon_grid:kit_created_from_banner', {
          kit_name: newKit.name,
          icon_saved: !!pending
        })
      } catch (e) {
        commit('setError', e.message || 'Failed to create Kit')
      } finally {
        commit('setSaving', false)
      }
    },

    async addToKitSubset({ commit, getters, rootGetters, dispatch }) {
      commit('setSaving', true)
      commit('setError', null)

      try {
        const kitToken = getters.selectedKitToken
        const kits = rootGetters['kits/currentKits'] || []
        const kit = kits.find((k) => k.token === kitToken)

        if (!kit) {
          throw new Error('Selected kit not found')
        }

        await dispatch('kits/fetchKit', kitToken, { root: true })

        // Re-read from store after fetch to get the freshest server data
        const freshKits = rootGetters['kits/currentKits'] || []
        const freshKit = freshKits.find((k) => k.token === kitToken)

        if (!freshKit) {
          throw new Error('Selected kit not found after refresh')
        }

        let subset
        if (freshKit.subset && freshKit.subset.icons) {
          subset = JSON.parse(JSON.stringify(freshKit.subset))
        } else {
          subset = { type: 'custom', icons: [] }
        }

        addIconsToSubset(subset, getters.collectedIcons)

        await dispatch('kits/updateKit', { subset, version: freshKit.version }, { root: true })
        // The backend rebuilds the npm package as a side effect of the kit update.

        const iconCount = getters.collectedIcons.length
        commit('clearAll')
        commit('setSuccessMessage', { kitName: freshKit.name, kitToken, iconCount })

        posthog?.capture('icon_grid:icons_saved_to_kit', { kit_name: freshKit.name, icon_count: iconCount })
      } catch (e) {
        commit('setError', e.message || 'Failed to add icons to kit')
      } finally {
        commit('setSaving', false)
      }
    }
  }
}
